// The activity card: a contribution calendar of every box ticked, drawn in
// blueprint blue. One SVG, right-anchored, so the newest week sits flush
// against the card's edge and the older ones march off the left; nothing
// has to be measured. Shades are opacity steps over one token.
import { useMemo, useState } from 'react';
import { activityTally } from '@shared/demo';
import type { Project } from '@shared/plan';
import { Card } from '@app/ui';
import { CELL, GAP, GRID_H, LABEL_H, PITCH, WEEKS, activityColumns, shade, startOfDay } from './activity';

export function ActivityCard({ projects }: { projects: readonly Project[] }) {
  const tally = useMemo(() => activityTally(projects, startOfDay), [projects]);
  return (
    <Card padded className="activity">
      <h3 className="activity-title">Activity</h3>
      <ActivityGrid tally={tally} />
    </Card>
  );
}

export function ActivityGrid({ tally, weeks = WEEKS }: { tally: Map<number, number>; weeks?: number }) {
  // Today, read once: the list is mounted afresh on every visit.
  const [today] = useState(() => startOfDay(Date.now()));
  const columns = useMemo(() => activityColumns(today, weeks), [today, weeks]);
  const width = weeks * PITCH - GAP;
  let total = 0;
  for (const n of tally.values()) total += n;
  return (
    <svg
      className="activity-grid" viewBox={`0 0 ${width} ${GRID_H}`} width="100%" height={GRID_H}
      preserveAspectRatio="xMaxYMid slice" role="img" aria-label={`Activity grid: ${total} ticked in the last ${weeks} weeks`}
    >
      {columns.map((c, col) => {
        const x = col * PITCH;
        return (
          <g key={c.weekStart}>
            {c.label && <text x={x} y={LABEL_H - 6} className="activity-month">{c.label}</text>}
            {c.days.map((day, row) => {
              if (day === null) return null;
              const n = tally.get(day) ?? 0;
              const y = LABEL_H + row * PITCH;
              return (
                <g key={row}>
                  <rect x={x} y={y} width={CELL} height={CELL} rx={3} className={`activity-cell s${shade(n)}`} />
                  {n > 0 && <path className="activity-mark" d={`M${x + 3.5} ${y + 3.5}l${CELL - 7} ${CELL - 7}M${x + CELL - 3.5} ${y + 3.5}l${7 - CELL} ${CELL - 7}`} />}
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}
